import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useApi from 'vl-common/src/hooks/useApi';
import { Task } from 'vl-common/src/types';
import { getMetaCaseQuery } from 'vl-common/src/schemas/getMetaCase';
import { Case } from 'vl-common/src/schemas/shared';

interface UseCompleteTaskModalProps {
  selectedTask: Task | undefined;
  isEligibilityCheck: boolean;
}

const useCompleteTaskModalEligibilityData = ({ selectedTask, isEligibilityCheck }: UseCompleteTaskModalProps) => {
  const { Api } = useApi();

  const { data: caseMeta } = useQuery(
    getMetaCaseQuery({
      pat_guid: selectedTask?.patient_guid || undefined
    })
  );

  const {
    data: patientData,
    isLoading: isPatientDataLoading,
    isFetched: isPatientDataFetched
  } = useQuery({
    queryKey: ['patient', selectedTask?.patient_guid],
    queryFn: async () => {
      const response = await Api.referrers.getPatient(selectedTask?.patient_guid, {
        pat_guid: selectedTask?.patient_guid
      });
      return response;
    },
    select: (data) => {
      return data?.data?.users[0];
    },
    enabled: !!selectedTask?.patient_guid && isEligibilityCheck
  });

  const {
    data: { patientCaseList, currentTaskStatus } = {},
    isLoading: patientCaseListIsLoading,
    isFetched: patientCaseListFetched
  } = useQuery({
    queryKey: ['caseList', patientData?.user_guid],
    queryFn: async () => {
      const response = await Api.cases.searchForCases({
        rows: 100,
        patient: patientData?.user_guid,
        first_incomplete: true,
        active: true
      });
      return response;
    },
    select: (data) => {
      const currentCase = data?.data?.cases.find((item: Case) => item.case_id === Number(selectedTask?.case_id));

      return {
        patientCaseList: data?.data?.cases || [],
        currentTaskStatus: currentCase?.eligibility_check_status_code
      };
    },
    enabled: !!patientData?.user_guid && isEligibilityCheck
  });

  const { data: eligibilityPolicyCheckList, isFetched: isEligibilityPolicyCheckListLoading } = useQuery({
    queryKey: ['checkEligibility', selectedTask?.case_id],
    queryFn: async () => {
      const response = await Api.meta.getEligibilityPolicies(selectedTask?.case_id as any);
      const policies = response?.data?.meta[0]?.eligibility_policies || [];
      return policies.map((item: any) => {
        return { label: item.policy_name, value: item.policy_code };
      });
    },
    enabled: !!selectedTask?.case_id && isEligibilityCheck
  });

  const { data: appliedEligibilityPolicies, isFetched: isAppliedEligibilityPoliciesLoading } = useQuery({
    queryKey: ['appliedEligibilityPolicies', selectedTask?.task_id],
    queryFn: async () => {
      const response = await Api.cases.getCaseEligibility(selectedTask?.case_id);
      const policies = response?.data?.case_eligibility?.[0]?.policies;
      return policies
        .filter(({ applied }: { applied: string }) => applied === 'true')
        .map(({ policy_code }: { policy_code: string }) => policy_code);
    },
    enabled: !!selectedTask?.task_id && isEligibilityCheck,
    gcTime: 0
  });

  const { data: referrerNotes, isFetched: isReferrerNotesLoading } = useQuery({
    queryKey: ['referrerNotes', selectedTask?.case_id],
    queryFn: async () => {
      const response = await Api.notes.getNotes({ case_id: selectedTask?.case_id, category: 'ELIGIBILITY', rows: 100 });
      const notes = response?.data?.patient_notes || [];
      if (notes.length === 0) {
        return '';
      } else {
        return String(notes[notes.length - 1]?.note_text || '');
      }
    },
    enabled: !!selectedTask?.case_id && isEligibilityCheck,
    gcTime: 0
  });

  const eligibilityCheckStatuses =
    caseMeta?.eligibility_check_status_codes?.map(
      ({ eligibility_check_status_code, eligibility_check_status_desc }) => ({
        value: eligibility_check_status_code,
        label: eligibility_check_status_desc
      })
    ) || [];

  const isFormDataLoaded =
    isPatientDataFetched &&
    patientCaseListFetched &&
    isEligibilityPolicyCheckListLoading &&
    isAppliedEligibilityPoliciesLoading &&
    isReferrerNotesLoading;

  return {
    isFormDataLoaded,
    caseMeta,
    patientData,
    isPatientDataLoading,
    patientCaseList,
    patientCaseListIsLoading,
    currentTaskStatus,
    eligibilityCheckStatuses,
    eligibilityPolicyCheckList,
    appliedEligibilityPolicies,
    referrerNotes
  };
};

interface ValidatedDataProp {
  claim_status: string;
  case_ref?: string;
  filter_tags?: string[];
  info_tags?: string[];
  membership_no?: string;
  policy_checks_fulfilled?: string[];
  virtual_only_reason?: string;
  referrer_notes?: string;
}

interface UseCompleteTaskModalEligibilitySubmissionProps {
  selectedTask: Task | undefined;
  claimStatus: string;
}

const useCompleteTaskModalSubmission = ({
  selectedTask,
  claimStatus
}: UseCompleteTaskModalEligibilitySubmissionProps) => {
  const { Api } = useApi();
  const queryClient = useQueryClient();

  // The mutate that POSTs an individual policy to the BE with applied/unapplied status
  const { mutateAsync: postEligibilityChanges, isPending: isPostEligibilityChangesLoading } = useMutation({
    mutationKey: ['saveEligibility'],
    mutationFn: async (data: { case_id: string; policyCode: string; applied: boolean }) => {
      const response = await Api.referrers.postEligibilityPolicy(data.case_id, data.policyCode, data.applied);
      return response;
    },
    onError: (error) => {
      throw error;
    }
  });

  // Splits the policies into applied/unapplied lists.
  // Loops through each item in each list and calls the mutate function
  const onSaveEligibilityChecks = async ({
    case_id,
    allPolicies,
    appliedPolicies
  }: {
    case_id: string;
    allPolicies: string[];
    appliedPolicies: string[];
  }) => {
    const unappliedPolicies = allPolicies.filter(
      (policy: any) => !appliedPolicies.includes(policy.value) // this works but is incorrect with typescript
    );
    return Promise.all([
      ...appliedPolicies.map((policy: any) => {
        return postEligibilityChanges({
          case_id,
          policyCode: policy,
          applied: true
        });
      }),
      ...unappliedPolicies.map((policy: any) => {
        return postEligibilityChanges({
          case_id,
          policyCode: policy.value,
          applied: false
        });
      })
    ]);
  };

  // Saves the eligibility task with the status and client case ID
  const {
    mutateAsync: updateEligibility,
    isPending: isUpdateEligibilityLoading,
    isSuccess: isUpdateEligibilitySuccess
  } = useMutation({
    mutationKey: ['saveEligibility', selectedTask?.patient_guid, selectedTask?.task_id],
    mutationFn: async (formData: {
      client_case_id: string | undefined;
      mem_no: string | undefined;
      caseId: string | undefined;
      patient_guid: string;
      virtual_only_reason: string | undefined;
    }) => {
      const response = await Api.task.putEligibility({
        caseRef: formData.client_case_id,
        membershipNo: formData.mem_no,
        eligibilityCheckStatusCode: claimStatus,
        caseId: formData.caseId,
        patientGuid: formData.patient_guid,
        virtualOnlyReason: formData.virtual_only_reason
      });
      return response;
    },
    onError: (error) => {
      throw error;
    }
  });

  // Saves the tags, both info and filter
  const { mutateAsync: saveTags, isPending: isSaveTagsLoading } = useMutation({
    mutationKey: ['saveTags', selectedTask?.patient_guid, selectedTask?.task_id],
    mutationFn: async (formData: { user_guid: string; infoTagsList: string[]; filterTagsList: string[] }) => {
      const response = await Api.tags.setTags(formData.user_guid, formData.infoTagsList, formData.filterTagsList);
      return response;
    },
    onError: (error) => {
      throw error;
    }
  });

  const { mutateAsync: saveReferrerNotes, isPending: isSaveReferrerNotesLoading } = useMutation({
    mutationKey: ['saveReferrerNotes', selectedTask?.task_id],
    mutationFn: async (formData: { case_id: string; note_text: string; category: string }) => {
      const response = await Api.notes.putNotes(formData);
      return response;
    },
    onError: (error) => {
      throw error;
    }
  });

  // Handles all API calls for saving an eligibility check
  // Does not complete the task
  const performEligibilitySave = async ({
    validatedData,
    eligibilityPolicyCheckList,
    case_id
  }: {
    validatedData: ValidatedDataProp;
    eligibilityPolicyCheckList: string[];
    case_id: number | undefined;
  }) => {
    // Save the individual eligibility policies
    try {
      await onSaveEligibilityChecks({
        case_id: String(case_id) || '',
        allPolicies: eligibilityPolicyCheckList,
        appliedPolicies: validatedData?.policy_checks_fulfilled || []
      });

      // Save the eligibility task and status
      await updateEligibility({
        client_case_id: validatedData.case_ref,
        mem_no: validatedData.membership_no,
        caseId: String(case_id),
        patient_guid: selectedTask?.patient_guid || '',
        virtual_only_reason: validatedData.virtual_only_reason
      });
      // Save the tags
      if (
        (validatedData?.info_tags && validatedData?.info_tags.length > 0) ||
        (validatedData?.filter_tags && validatedData?.filter_tags.length > 0)
      ) {
        await saveTags({
          user_guid: selectedTask?.patient_guid || '',
          infoTagsList: validatedData?.info_tags || [],
          filterTagsList: validatedData?.filter_tags || []
        });
      }
      // Save the referrer notes
      if (case_id && validatedData?.referrer_notes) {
        await saveReferrerNotes({
          case_id: String(case_id),
          note_text: validatedData.referrer_notes,
          category: 'ELIGIBILITY'
        });
      }

      await queryClient.invalidateQueries({
        queryKey: ['patient', selectedTask?.patient_guid]
      });
      await queryClient.invalidateQueries({
        queryKey: ['referrerNotes', selectedTask?.case_id]
      });
      await queryClient.invalidateQueries({
        queryKey: ['caseList', selectedTask?.patient_guid]
      });
    } catch (e) {
      console.debug(e);
      throw e;
    }
  };

  const { mutateAsync: completeTask, isPending: isCompleteTaskLoading } = useMutation({
    mutationKey: ['completeTask', selectedTask?.task_id],
    mutationFn: async () => {
      const response = await Api.task.complete({
        task_id: selectedTask?.task_id,
        completion_notes: ''
      });
      return response;
    },
    onError: (error) => {
      throw error;
    }
  });

  const isEligibilitySaveLoading =
    isPostEligibilityChangesLoading || isUpdateEligibilityLoading || isSaveTagsLoading || isSaveReferrerNotesLoading;

  return {
    isEligibilitySaveLoading,
    isUpdateEligibilitySuccess,
    performEligibilitySave,
    completeTask,
    isCompleteTaskLoading
  };
};

export { useCompleteTaskModalEligibilityData, useCompleteTaskModalSubmission };
